import { AxiosResponse } from 'axios';

import { apiGatewayUrl } from './config';
import { ApiGatewayClient } from './utils/api-gateway-client.util';
import { ContentTypes, HttpMethod } from './utils/enums';
import {
    AlarmBody,
    ProductPricingParams,
    SimulationData,
} from './utils/interfaces';
import {
    dataResponseNotAllowedMessage,
    serviceReplace,
} from './Views/CalculatorError/error.constants';
import { MatchDataAlarmBody } from './Views/CalculatorError/error.types';

/**
 * Class responsible for manage the API calls and data request of the App
 */
export class AppService {
    private readonly urlStructure = `${apiGatewayUrl}/product-pricing/catalogs/mx/`;
    private client: ApiGatewayClient;
    private headers = {
        'Content-Type': ContentTypes.json,
        Accept: ContentTypes.json,
    };
    /**
     * Instantiates a new identified ApiGatewayClient
     */
    constructor() {
        this.client = new ApiGatewayClient();
    }

    /**
     * Retrieves the financial products of a brand
     * @param brand client consumer of the widget
     */
    public async fetchFinancialProducts(
        brand: string,
        type: string,
        isPFA: boolean,
        yearModel?: string,
    ): Promise<AxiosResponse> {
        const yearModelParam = yearModel ? `/${yearModel}` : '';
        const url = `${
            this.urlStructure
        }financial-products/${type}/${brand.toLowerCase()}/${isPFA}${yearModelParam}`;
        const request = {
            method: HttpMethod.get,
            url,
            headers: this.headers,
        };
        const result = await this.client.sendRequest(request);
        return result;
    }

    /**
     * Retrieves the vehicle models of a brand
     * @param brand client consumer of the widget
     */
    public async fetchVehicleModels(
        brand: string,
        type: string,
        isPFA: boolean,
    ): Promise<AxiosResponse> {
        const url = `${
            this.urlStructure
        }vehicle-models/${type}/${brand.toLowerCase()}/${isPFA}`;
        const request = {
            method: HttpMethod.get,
            url,
            headers: this.headers,
        };
        const result = await this.client.sendRequest(request);
        return result;
    }

    /**
     * With the info managed by the financial calculator this retrieves the product pricing
     *
     * @param brand client consumer of the widget
     * @param body data to set the product pricing simulation
     */
    public async fetchSimulation(
        brand: string,
        isPFA: boolean,
        body: ProductPricingParams,
    ): Promise<AxiosResponse> {
        const url = `${apiGatewayUrl}/product-pricing/pricing/mx/simulation/${brand.toLowerCase()}/${isPFA}/create`;
        const request = {
            url,
            method: HttpMethod.post,
            data: body,
            headers: this.headers,
        };

        const result = await this.client.sendRequest<SimulationData>(request);
        const errorMessage = dataResponseNotAllowedMessage.replace(
            serviceReplace,
            'simulation-service',
        );
        if (result.data) {
            const data = result.data;
            const getNullOrZero = Object.keys(data).some(
                (item) =>
                    data[item] === 0 ||
                    data[item] === null ||
                    data[item] === undefined,
            );

            if (getNullOrZero) throw new Error(errorMessage);
        } else {
            throw new Error(errorMessage);
        }

        return result;
    }

    /**
     * This service send the data to the alarms api.
     *
     * @param body Object.
     * @returns Promise<AxiosResponse>.
     */
    public async fetchAlarms(body: AlarmBody): Promise<void> {
        const url = `${apiGatewayUrl}/alarms`;
        const request = {
            url,
            method: HttpMethod.post,
            data: body,
            headers: this.headers,
        };

        this.client.sendRequest(request);
    }

    /**
     * This service send the data to the alarms api.
     *
     * @param body Object.
     * @returns Promise<AxiosResponse>.
     */
    public async fetchMismatchAlarms(body: MatchDataAlarmBody): Promise<void> {
        const url = `${apiGatewayUrl}/mismatch-alarms`;
        const request = {
            url,
            method: HttpMethod.post,
            data: body,
            headers: this.headers,
        };

        this.client.sendRequest(request);
    }

    /**
     * Retrieves external data of used vehicle according to a brand and a vehicle id
     * @param brand client consumer of the widget
     * @param vehicleId external vehicle id
     */
    public async fetchExternalData(
        brand: string,
        vehicleId: string,
    ): Promise<AxiosResponse> {
        const url = `${apiGatewayUrl}/mx/external-data/used/${brand.toLowerCase()}/${vehicleId}`;
        const request = {
            method: HttpMethod.get,
            url,
            headers: this.headers,
        };
        const result = await this.client.sendRequest(request);
        return result;
    }
}
